import { Box, Skeleton } from '@mui/material';
import { useState } from 'react';
import { KitButton, KitList, toneColors } from './MobileKit';
import MobileSheet from './MobileSheet';
import { protoIcon } from './ProtoIcon';
import {
    KitAvatar,
    KitEmpty,
    KitFab,
    KitSearch,
    KitStickyToolbar,
    KitToolbarButton,
} from './MobileKitExtras';
import { useAppBarAction } from '../layout/AppBarActions';
import { APP_BAR_OFFSET } from '../layout/mobileShell';

/**
 * Lista CRUD simples do shell mobile — `.sticky-top > .toolbar > .search` + `.list > .item`
 * + `.fab` dos protótipos de Categorias, Serviços, Veículos, Fornecedores e Montadoras.
 * Só apresentação: dados, busca, paginação, permissões e ações vêm da tela que a usa.
 */

const SlidersIcon = protoIcon('sliders');
const PlusIcon = protoIcon('plus');
const ChevronRight = protoIcon('right');
const DothIcon = protoIcon('doth');

// `.action-list .item` dentro da folha (Mais / ações da linha)
export function MobileActionItem({ icon: Icon, label, onClick, destructive, chevron = true }) {
    return (
        <Box
            component="button"
            type="button"
            onClick={onClick}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                p: '12px 4px',
                minHeight: 60,
                width: '100%',
                boxSizing: 'border-box',
                border: 0,
                background: 'none',
                textAlign: 'left',
                fontFamily: 'inherit',
                color: 'text.primary',
                cursor: 'pointer',
                '&:active': { backgroundColor: 'background.subtle' },
            }}
        >
            <Box
                sx={(theme) => ({
                    width: 40,
                    height: 40,
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    backgroundColor: destructive ? theme.palette.error.bg : theme.palette.info.bg,
                    color: destructive ? theme.palette.error.main : theme.palette.primary.main,
                })}
            >
                <Icon size={20} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, fontWeight: 700, fontSize: 14, lineHeight: 1.4, color: destructive ? 'error.main' : 'text.primary' }}>{label}</Box>
            {chevron && <Box sx={{ color: 'text.disabled', display: 'inline-flex', flexShrink: 0 }}><ChevronRight size={18} /></Box>}
        </Box>
    );
}

// `.item` — ícone (`.ico` + tom) ou avatar, título (`.t`), subtítulo (`.s`), fim (`.end`) e chevron.
export function MobileListRow({ icon: Icon, tone, avatar, title, sub, end, chevron, onClick, sx }) {
    const clickable = Boolean(onClick);
    return (
        <Box
            component={clickable ? 'a' : 'div'}
            role={clickable ? 'link' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={clickable ? onClick : undefined}
            onKeyDown={clickable ? (e) => { if (e.key === 'Enter') onClick(); } : undefined}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                p: '13px 14px',
                minHeight: 60,
                boxSizing: 'border-box',
                borderBottom: '1px solid',
                borderColor: 'divider',
                color: 'text.primary',
                textDecoration: 'none',
                cursor: clickable ? 'pointer' : 'default',
                '&:last-child': { borderBottom: 0 },
                ...(clickable ? { '&:active': { backgroundColor: 'background.subtle' } } : {}),
                ...sx,
            }}
        >
            {avatar && <KitAvatar size={36} text={avatar.text} tone={avatar.tone} />}
            {Icon && (
                <Box
                    component="span"
                    sx={(theme) => {
                        const [bg, fg] = toneColors(theme, tone);
                        return { width: 40, height: 40, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: bg, color: fg, flexShrink: 0 };
                    }}
                >
                    <Icon size={20} />
                </Box>
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title || '—'}</Box>
                {sub !== undefined && sub !== null && sub !== '' && (
                    <Box sx={{ fontSize: 12, color: 'text.disabled', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', mt: '1px', lineHeight: 1.4 }}>{sub}</Box>
                )}
            </Box>
            {end && <Box sx={{ textAlign: 'right', flexShrink: 0 }}>{end}</Box>}
            {chevron && <Box sx={{ color: 'text.disabled', display: 'inline-flex', flexShrink: 0 }}><ChevronRight size={18} /></Box>}
        </Box>
    );
}

/**
 * Props:
 * - items, getKey(item), getRow(item) -> props de `MobileListRow` (sem onClick)
 * - search/onSearchChange/searchPlaceholder/searchLabel
 * - filter?: { children, onOpen, onApply, onClear, active } — botão de filtros + folha
 * - topSlot?: nó entre a busca e a lista (ex.: chips)
 * - rowActions?(item) -> [{ label, icon, destructive, onClick }]; rowTitle(item) — folha da linha
 * - onOpen?(item) — toque direto na linha quando não há `rowActions`
 * - moreItems?: [{ label, icon, onClick }] — ação "Mais" (⋯) da barra superior
 * - fabLabel/onNew — botão flutuante (omita sem permissão)
 */
export default function MobileCrudList({
    items,
    getKey,
    getRow,
    loading,
    search,
    onSearchChange,
    searchPlaceholder,
    searchLabel,
    hideSearch,
    filter,
    topSlot,
    emptyIcon,
    emptyTitle,
    emptyText,
    hasMore,
    onLoadMore,
    fabLabel,
    onNew,
    moreItems = [],
    rowActions,
    rowTitle,
    onOpen,
}) {
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [moreOpen, setMoreOpen] = useState(false);
    const [selected, setSelected] = useState(null);

    useAppBarAction({
        enabled: moreItems.length > 0,
        label: 'Mais',
        icon: DothIcon,
        onClick: () => setMoreOpen(true),
    });

    const openItem = (item) => {
        if (rowActions) {
            if (rowActions(item).length > 0) setSelected(item);
            return;
        }
        if (onOpen) onOpen(item);
    };
    const clickable = Boolean(rowActions || onOpen);
    const actions = selected && rowActions ? rowActions(selected) : [];

    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }}>
            {!hideSearch && (
                <KitStickyToolbar top={APP_BAR_OFFSET}>
                    <KitSearch
                        value={search}
                        onChange={(e) => onSearchChange(e.target.value)}
                        placeholder={searchPlaceholder}
                        inputProps={{ 'aria-label': searchLabel || searchPlaceholder, inputMode: 'search', enterKeyHint: 'search' }}
                    />
                    {filter && (
                        <KitToolbarButton
                            aria-label="Filtros"
                            onClick={() => {
                                if (filter.onOpen) filter.onOpen();
                                setFiltersOpen(true);
                            }}
                            sx={filter.active ? { color: 'primary.main', borderColor: 'primary.main' } : undefined}
                        >
                            <SlidersIcon size={20} />
                        </KitToolbarButton>
                    )}
                </KitStickyToolbar>
            )}

            {topSlot}

            <Box sx={{ mt: '12px' }} />

            {loading && items.length === 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {[1, 2, 3, 4].map((n) => <Skeleton key={n} variant="rounded" height={60} sx={{ borderRadius: '16px' }} />)}
                </Box>
            )}

            {!loading && items.length === 0 && (
                <KitEmpty icon={emptyIcon} title={emptyTitle}>{emptyText}</KitEmpty>
            )}

            {items.length > 0 && (
                <KitList>
                    {items.map((item) => (
                        <MobileListRow key={getKey(item)} {...getRow(item)} onClick={clickable ? () => openItem(item) : undefined} />
                    ))}
                </KitList>
            )}

            {hasMore && (
                <KitButton block sx={{ mt: '14px' }} onClick={onLoadMore} disabled={loading}>
                    {loading ? 'Carregando…' : 'Carregar mais'}
                </KitButton>
            )}

            {fabLabel && onNew && <KitFab label={fabLabel} icon={PlusIcon} onClick={onNew} />}

            {filter && (
                <MobileSheet
                    open={filtersOpen}
                    onClose={() => setFiltersOpen(false)}
                    title="Filtros"
                    footer={(
                        <>
                            <KitButton
                                sx={{ flex: 1 }}
                                onClick={() => {
                                    setFiltersOpen(false);
                                    filter.onClear();
                                }}
                            >
                                Limpar
                            </KitButton>
                            <KitButton
                                variant="primary"
                                sx={{ flex: 1 }}
                                onClick={() => {
                                    setFiltersOpen(false);
                                    filter.onApply();
                                }}
                            >
                                Aplicar
                            </KitButton>
                        </>
                    )}
                >
                    {filter.children}
                </MobileSheet>
            )}

            <MobileSheet open={moreOpen} onClose={() => setMoreOpen(false)} title="Mais">
                {moreItems.map((item) => (
                    <MobileActionItem
                        key={item.label}
                        icon={item.icon}
                        label={item.label}
                        onClick={() => {
                            setMoreOpen(false);
                            item.onClick();
                        }}
                    />
                ))}
            </MobileSheet>

            <MobileSheet open={Boolean(selected)} onClose={() => setSelected(null)} title={selected && rowTitle ? rowTitle(selected) : ''}>
                {actions.map((action) => (
                    <MobileActionItem
                        key={action.label}
                        icon={action.icon}
                        label={action.label}
                        destructive={action.destructive}
                        chevron={!action.destructive}
                        onClick={() => {
                            const target = selected;
                            setSelected(null);
                            action.onClick(target);
                        }}
                    />
                ))}
            </MobileSheet>
        </Box>
    );
}
